import React, { useState } from "react";
import { Alert, Box, Button, Drawer, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useLazyGetSyncTaskStatusQuery } from "state/rtkQuery/sync/syncQueries";

import { formatDateTime, isActiveState, normalizeApiError, STATE_LABEL, timeAgo } from "./syncFormat";
import { CallerCell, headCellSx, JsonBlock, mono, OutcomePill, Panel, Pill, StatePill } from "./SyncParts";
import { useSyncTasks } from "./SyncTasksContext";
import { useTaskOutcome } from "./useTaskOutcome";

export const TaskDrawer = ({ taskId, onClose }) => {
  const { tasks, refreshTask } = useSyncTasks();
  const task = tasks.find((item) => item.id === taskId);
  const outcome = useTaskOutcome(task); // before the early return, so the hook order never changes
  if (!task) return null;
  const raw = { task_id: task.id, status: task.state, result: task.result, error: task.error };
  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 560 } } }}>
      <Box sx={{ p: "18px 22px", borderBottom: "1px solid #e2e8f0" }}>
        <Typography sx={{ color: "#6a9a1e", fontSize: 11.5, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" }}>Task</Typography>
        <Typography sx={{ fontSize: 17, fontWeight: 700, mt: 0.25 }}>{task.title}</Typography>
        <Typography sx={{ ...mono, mt: 0.5 }}>{task.id}</Typography>
      </Box>
      <Box sx={{ p: "20px 22px", overflow: "auto", flex: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
          <StatePill state={task.state} />
          <OutcomePill task={task} />
          <Typography sx={{ color: "#64748b", fontSize: 12.5 }}>{STATE_LABEL[task.state]}</Typography>
        </Box>
        <Box sx={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", p: "10px 14px", fontSize: 13, mb: 2 }}>
          {[
            ["Task", <span style={mono}>{task.taskName}</span>],
            ["Queue", <span style={mono}>{task.queue}</span>],
            ["Scope", task.scope],
            ["Started", `${formatDateTime(task.startedAt)} (${timeAgo(task.startedAt)})`],
          ].map(([label, value]) => (
            <Box key={label} sx={{ display: "flex", justifyContent: "space-between", py: 0.4, gap: 2 }}>
              <span style={{ color: "#64748b" }}>{label}</span>
              <b style={{ fontWeight: 600, textAlign: "right" }}>{value}</b>
            </Box>
          ))}
        </Box>

        {outcome.kind === "skipped" && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            It finished, but skipped the work{task.result?.reason ? `: ${task.result.reason}` : ""}. Run it again with "Run even if it ran recently" if you really need a repeat.
          </Alert>
        )}
        {outcome.kind === "problem" && <Alert severity="error" sx={{ mb: 2 }}>The worker finished but reported a problem. See the details below.</Alert>}
        {outcome.kind === "dispatched" && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <b>Dispatch finished.</b> The follow-up jobs it queued may still be running. This does not mean every item is done.
          </Alert>
        )}
        {task.unknownOrExpired && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The service has no live status and no retained record for this task: it is unknown or has expired. That doesn't prove it never ran.
          </Alert>
        )}
        {(task.owner || task.triggeredBy) && (
          <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13, mb: 2 }} data-testid="task-caller">
            <span style={{ color: "#64748b" }}>Triggered by</span>
            <CallerCell source={{ owner: task.owner, triggered_by: task.triggeredBy }} />
          </Box>
        )}
        {task.state === "PENDING" && !task.unknownOrExpired && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Waiting for a worker. If this stays pending for a long time, the result may have expired or the task was never picked up.
            {task.stalePending && " Automatic checking has stopped: use Refresh status."}
          </Alert>
        )}
        {task.lookupFailed && <Alert severity="warning" sx={{ mb: 2 }}>Couldn't read the latest status: {task.lookupMessage}. That doesn't mean the task failed.</Alert>}

        <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Raw response</Typography>
        <JsonBlock value={raw} />
        <Box sx={{ mt: 1.5 }}>
          <Button size="small" variant="outlined" onClick={() => refreshTask(task.id)} sx={{ textTransform: "none" }}>↻ Refresh status</Button>
        </Box>
      </Box>
      <Box sx={{ p: "14px 22px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", background: "#fafbfc" }}>
        <Button onClick={onClose}>Close</Button>
      </Box>
    </Drawer>
  );
};

const TasksTab = ({ onOpenTask }) => {
  const { tasks, clearFinished } = useSyncTasks();
  const [lookupId, setLookupId] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [fetchStatus, { isFetching }] = useLazyGetSyncTaskStatusQuery();

  const lookup = async () => {
    const id = lookupId.trim();
    if (!id) return;
    const response = await fetchStatus({ taskId: id }, false);
    setLookupResult(response.isError ? { error: normalizeApiError(response.error).message } : { id, data: response.data });
  };

  return (
    <>
      <Panel
        title="Task tracker"
        subtitle="Runs submitted from this browser"
        actions={
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField size="small" placeholder="Look up any task ID" value={lookupId} onChange={(event) => setLookupId(event.target.value)} sx={{ width: 300 }} />
            <Button variant="outlined" onClick={lookup} disabled={!lookupId.trim() || isFetching} sx={{ textTransform: "none" }}>Check status</Button>
            <Button onClick={clearFinished} disabled={!tasks.some((task) => !isActiveState(task.state))} sx={{ textTransform: "none" }}>Clear finished</Button>
          </Box>
        }
      >
        {lookupResult && (
          <Box sx={{ p: 2, borderBottom: "1px solid #f1f5f9" }} data-testid="lookup-result">
            {lookupResult.error ? (
              <Alert severity="error">{lookupResult.error}</Alert>
            ) : (
              <>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                  <StatePill state={lookupResult.data?.status} />
                  <Typography sx={mono}>{lookupResult.id}</Typography>
                  {lookupResult.data?.status === "PENDING" && <Typography sx={{ color: "#64748b", fontSize: 12.5 }}>Waiting for a worker, or this ID is unknown or expired.</Typography>}
                </Box>
                <JsonBlock value={lookupResult.data} />
              </>
            )}
          </Box>
        )}
        {tasks.length === 0 ? (
          <Typography sx={{ p: 4, textAlign: "center", color: "#94a3b8" }}>Nothing run from this browser yet. Start an action and it appears here.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Action", "Task ID", "Scope", "Queue", "Status", "Outcome", "Started"].map((label) => (
                  <TableCell key={label} sx={headCellSx}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((task) => (
                <TableRow key={task.id} hover sx={{ cursor: "pointer" }} onClick={() => onOpenTask(task.id)} data-testid="task-row">
                  <TableCell sx={{ fontWeight: 600 }}>{task.title}</TableCell>
                  <TableCell sx={mono}>{task.id.slice(0, 8)}…</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{task.scope}</TableCell>
                  <TableCell sx={mono}>{task.queue}</TableCell>
                  <TableCell><StatePill state={task.state} />{task.lookupFailed && <Pill tone="warn" sx={{ ml: 0.5 }}>check failed</Pill>}</TableCell>
                  <TableCell><OutcomePill task={task} /></TableCell>
                  <TableCell sx={{ color: "#64748b", fontSize: 12.5 }}>{timeAgo(task.startedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <Panel title="How to read a status" sx={{ mt: 2 }}>
        <Box sx={{ p: "14px 20px", display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 2, fontSize: 12.8 }}>
          <Box><StatePill state="PENDING" still /><p style={{ color: "#64748b" }}>Waiting for a worker, or the service has no record of it (the result can also have expired).</p></Box>
          <Box><StatePill state="STARTED" still /><p style={{ color: "#64748b" }}>A worker is running it. Long jobs (usage, reports) can take a while.</p></Box>
          <Box><StatePill state="SUCCESS" still /><p style={{ color: "#64748b" }}>The worker finished. Check the outcome: it may have <b>skipped</b> the work or reported a problem.</p></Box>
          <Box><StatePill state="FAILURE" still /><p style={{ color: "#64748b" }}>The task failed. See the error. Retrying isn't offered here.</p></Box>
        </Box>
      </Panel>
    </>
  );
};

export default TasksTab;
